import { Injectable } from '@angular/core';
import { Observable, from, of, EMPTY, forkJoin, defer } from 'rxjs';
import {
  mergeMap,
  catchError,
  map,
  switchMap,
  tap,
  finalize,
  defaultIfEmpty,
  takeWhile
} from 'rxjs/operators';
import {
  TileProcessingItem,
  TileProcessingStatus,
  ProcessingState
} from '../../shared/models/csv-tile';
import { TilesService } from '../../../../core/services/tiles.service';
import { MetadataService } from '../../../../core/services/metadata.service';
import { DevicesService } from '../../../../core/services/devices.service';
import { CsvTileImporterService } from '../../shared/services/csv-tile-importer.service';
import { Tile, TileId, TileFolderDisplayMode, TILE_DEFAULT_WRITE_PRIORITY } from '../../../../shared/models/tile';
import { DeviceId, DeviceType } from '../../../../shared/models/device';
import { SimpleAnalogStructure } from '../../../../shared/models/structure';
import { makeFormulaDevice } from '../../../../shared/models/formula-device';
import { HttpErrorResponse } from '@angular/common/http';
import { UtilsService } from '../../../../core/services/utils.service';
import { AppId } from '../../../../shared/models/app';
import { SuperFolderStructure } from '../../../../shared/models/structure';
import { BaseBatchProcessorService } from '../../shared/services/base-batch-processor.service';
import { CsvFieldId } from '../../shared/constants/csv-columns.constants';
import { TagService } from '../../../../core/services/tag.service';
import { Tag, TagAssignment } from '../../../../shared/models/tag';
import { ObjectType } from '../../../../shared/models/object.model';
import { TAG_PROFILES } from '../../../../shared/constants/tag-profiles.constants';

/**
 * Service for batch creating tiles from CSV
 * Extends BaseBatchProcessorService for common batch processing infrastructure
 * Implements two-phase processing:
 * 1. Create tiles + link to apps (concurrent - no race conditions)
 * 2. Link to folders by parent (sequential per folder - avoids race conditions)
 */
@Injectable()
export class BulkTileCreationService extends BaseBatchProcessorService {

  private labelToTagId = new Map<string, string>(); // label.toUpperCase() -> TagID
  private allUserTags: Tag[] = [];

  constructor(
    private tilesService: TilesService,
    private metadataService: MetadataService,
    private devicesService: DevicesService,
    private csvMapperService: CsvTileImporterService,
    private tagService: TagService
  ) {
    super();
  }

  startProcessing(items: TileProcessingItem[], deviceId?: DeviceId): void {
    if (this.processingState() === ProcessingState.PROCESSING ||
        this.processingState() === ProcessingState.PAUSED) {
      return;
    }

    this.labelToTagId.clear();
    this.allUserTags = [];

    this.initializeProcessing(items);
    this.restartProcessing(items);
  }

  protected restartProcessing(items: TileProcessingItem[]): void {
    this.preResolveTagsAndFacets(items).pipe(
      switchMap(() => from(items).pipe(
        takeWhile(() => this.processingState() === ProcessingState.PROCESSING),
        mergeMap(item => this.createTileAndLinkApps(item), this.config.batchSize),
      )),
      finalize(() => {
        if (this.processingState() === ProcessingState.PROCESSING) {
          this.linkTilesToFolders(this.processingItems());
        }
      })
    ).subscribe();
  }

  private preResolveTagsAndFacets(items: TileProcessingItem[]): Observable<void> {
    const uniqueLabels = new Set<string>();

    for (const item of items) {
      this.csvMapperService.parseTagLabels((item.csvData as any)[CsvFieldId.TAGS])
        .forEach(l => uniqueLabels.add(l));
    }

    if (uniqueLabels.size === 0) {
      return of(undefined);
    }

    return this.tagService.getUserTags$().pipe(
      tap(existingTags => { this.allUserTags = existingTags; }),
      switchMap(existingTags => {
        const labelsToCreate: string[] = [];
        for (const label of uniqueLabels) {
          const found = existingTags.find(t => t.Label.toUpperCase() === label.toUpperCase());
          if (found) {
            this.labelToTagId.set(label.toUpperCase(), found.ID);
          } else {
            labelsToCreate.push(label);
          }
        }

        if (labelsToCreate.length === 0) {
          return of(undefined);
        }

        return forkJoin(labelsToCreate.map(label =>
          this.tagService.createOrFindTag$(label).pipe(
            tap(tag => this.labelToTagId.set(label.toUpperCase(), tag.ID)),
            catchError(() => of(null))
          )
        )).pipe(map(() => undefined));
      }),
      map(() => undefined),
      catchError(() => of(undefined))
    );
  }

  private createTileAndLinkApps(item: TileProcessingItem): Observable<any> {
    // Only mark as processing if we're actually in processing state
    if (this.processingState() !== ProcessingState.PROCESSING) {
      return EMPTY;
    }

    this.updateStatus(item.id, TileProcessingStatus.PROCESSING);

    if (!item.tile) {
      this.updateStatus(item.id, TileProcessingStatus.ERROR_GENERAL, 'Tile validation failed');
      return EMPTY;
    }

    const isRetryOfExistingTile = !!item.tileId;

    // Only reset link tracking for new tiles, preserve it for retries
    if (!isRetryOfExistingTile) {
      item.linkedApps = [];
      item.linkedSuperTiles = [];
      item.failedLinks = { apps: [], superTiles: [] };
    }

    // If tile already exists (retry of ERROR_LINKING), skip creation and go directly to linking
    const tileCreationOrExisting$ = isRetryOfExistingTile
      ? of(item.tileId!)
      : this.tilesService.createTile$(item.tile).pipe(
          takeWhile(() => this.processingState() === ProcessingState.PROCESSING),
          tap(tileId => {
            item.tileId = tileId;
            this.storeMetadataInBackground(tileId, item);
          })
        );

    return tileCreationOrExisting$.pipe(
      switchMap(tileId => item.formulaConfig && !isRetryOfExistingTile
        ? this.setupFormulaTile$(tileId, item)
        : of(tileId)
      ),
      switchMap(tileId => {
        // Get all app IDs from CSV
        const allAppIds = this.csvMapperService.parseLinkedAppIds(item.csvData[CsvFieldId.LINKED_APP_IDS]);

        // For retries, only link the failed ones; for new tiles, link all
        const appIdsToLink = isRetryOfExistingTile
          ? (item.failedLinks?.apps || [])
          : allAppIds;

        return appIdsToLink.length > 0 ? this.linkItemToApps(item, appIdsToLink).pipe(map(() => tileId)) : of(tileId);
      }),
      switchMap(tileId => this.assignTileTagsIfNeeded(item, tileId)),
      tap(() => {
        const superTileIds = this.csvMapperService.parseLinkedSuperTileIds(item.csvData[CsvFieldId.LINKED_SUPER_TILE_IDS]);
        if (superTileIds.length === 0) {
          this.updateItemStatusAfterLinking(item);
        } else {
          this.updateStatus(item.id, TileProcessingStatus.AWAITING_FOLDER_LINKING, undefined, item.tileId);
        }
      }),
      catchError(error => {
        this.handleError(item, error);
        return EMPTY;
      })
    );
  }

  private linkTilesToFolders(items: TileProcessingItem[]): void {
    const folderGroups = new Map<TileId, TileProcessingItem[]>();

    items.forEach(item => {
      if (!item.tileId) {
        return;
      }

      // Skip items that failed during creation — their error status must not be overridden
      if (item.status === TileProcessingStatus.ERROR_GENERAL ||
          item.status === TileProcessingStatus.ERROR_PERMISSIONS) {
        return;
      }

      // Get all supertile IDs from CSV
      const allSuperTileIds = this.csvMapperService.parseLinkedSuperTileIds(item.csvData[CsvFieldId.LINKED_SUPER_TILE_IDS]);

      // For retries, only link to failed supertiles; for new items, link to all
      const isRetry = (item.linkedSuperTiles && item.linkedSuperTiles.length > 0) ||
                      (item.failedLinks?.superTiles && item.failedLinks.superTiles.length > 0);
      const superTileIdsToLink = isRetry
        ? (item.failedLinks?.superTiles || [])
        : allSuperTileIds;

      superTileIdsToLink.forEach(parentId => {
        if (!folderGroups.has(parentId)) {
          folderGroups.set(parentId, []);
        }
        folderGroups.get(parentId)!.push(item);
      });
    });

    const folderLinkOps = Array.from(folderGroups.entries()).map(([parentId, children]) =>
      this.linkChildrenToFolder(parentId, children)
    );

    from(folderLinkOps).pipe(
      takeWhile(() => this.processingState() === ProcessingState.PROCESSING),
      mergeMap(op => op, this.config.batchSize),
      finalize(() => {
        // Only complete if we're still processing (not paused)
        if (this.processingState() === ProcessingState.PROCESSING) {
          this.stopProcessing();
        }
      })
    ).subscribe();
  }

  private linkItemToApps(item: TileProcessingItem, appIds: AppId[]): Observable<void> {
    if (!item.tileId) {
      return of(undefined);
    }
    this.updateStatus(item.id, TileProcessingStatus.LINKING_APPS, undefined, item.tileId);

    const linkOps = appIds.map(appId =>
      this.tilesService.linkWithApp$(item.tileId!, [appId]).pipe(
        tap(() => {
          // Add to linked apps
          item.linkedApps!.push(appId);
          // Remove from failed links if it was there (successful retry)
          const failedIndex = item.failedLinks!.apps!.indexOf(appId);
          if (failedIndex > -1) {
            item.failedLinks!.apps!.splice(failedIndex, 1);
          }
        }),
        catchError(() => {
          // Only add to failed links if not already there (avoid duplicates on retry)
          if (!item.failedLinks!.apps!.includes(appId)) {
            item.failedLinks!.apps!.push(appId);
          }
          return EMPTY;
        })
      )
    );

    return from(linkOps).pipe(
      takeWhile(() => this.processingState() === ProcessingState.PROCESSING),
      mergeMap(op => op, this.config.batchSize),
      finalize(() => {}), // Ensure completion is detected
      map(() => undefined),
      // Ensure we always emit at least once, even if all links failed
      defaultIfEmpty(undefined)
    );
  }

  private linkChildrenToFolder(parentId: TileId, children: TileProcessingItem[]): Observable<void> {
    const childIds = children.map(c => c.tileId!).filter(id => !!id);
    if (childIds.length === 0) {
      return of(undefined);
    }

    children.forEach(child => {
      this.updateStatus(child.id, TileProcessingStatus.LINKING_SUPER_TILES, undefined, child.tileId);
    });

    // Fetch parent with FGA children (excludes deleted tiles)
    return this.tilesService.fetchTilesAndDescendants$([parentId]).pipe(
      takeWhile(() => this.processingState() === ProcessingState.PROCESSING),
      switchMap(tiles => {
        const parentTile = tiles.find(t => t.ID === parentId);
        if (!parentTile) {
          throw new Error(`Parent tile ${parentId} not found`);
        }

        const existingChildren = tiles.filter(t => t.ID !== parentId).map(t => t.ID);
        const allChildren = [...new Set([...existingChildren, ...childIds])];

        // Update FGA + Structure.TileIDs
        return this.tilesService.replaceChildrenOfTile$(parentId, allChildren).pipe(
          switchMap(() => {
            const structure = parentTile.Structure as SuperFolderStructure;
            if (!structure) {
              parentTile.Structure = {
                DisplayMode: TileFolderDisplayMode.Default,
                Priority: TILE_DEFAULT_WRITE_PRIORITY,
                TileIDs: allChildren
              };
            } else {
              structure.TileIDs = allChildren;
            }
            return this.tilesService.saveTile$(parentTile);
          }),
          tap(() => {
            children.forEach(child => {
              // Add to linked supertiles
              child.linkedSuperTiles!.push(parentId);
              // Remove from failed links if it was there (successful retry)
              const failedIndex = child.failedLinks!.superTiles!.indexOf(parentId);
              if (failedIndex > -1) {
                child.failedLinks!.superTiles!.splice(failedIndex, 1);
              }
            });
          })
        );
      }),
      catchError(() => {
        children.forEach(child => {
          // Only add to failed links if not already there (avoid duplicates on retry)
          if (!child.failedLinks!.superTiles!.includes(parentId)) {
            child.failedLinks!.superTiles!.push(parentId);
          }
        });
        return EMPTY;
      }),
      finalize(() => {
        children.forEach(child => this.updateItemStatusAfterLinking(child));
      }),
      map(() => undefined)
    );
  }

  private updateItemStatusAfterLinking(item: TileProcessingItem): void {
    // Creation errors take priority — never let a successful link overwrite them
    if (item.status === TileProcessingStatus.ERROR_GENERAL ||
        item.status === TileProcessingStatus.ERROR_PERMISSIONS) {
      return;
    }

    const failedApps = item.failedLinks?.apps?.length || 0;
    const failedSuperTiles = item.failedLinks?.superTiles?.length || 0;

    if (failedApps === 0 && failedSuperTiles === 0) {
      if (item.tagLinkingFailed) {
        this.updateStatus(item.id, TileProcessingStatus.ERROR_TAG_LINKING, item.error, item.tileId);
      } else {
        this.updateStatus(item.id, TileProcessingStatus.CREATED, undefined, item.tileId);
      }
      return;
    }

    const errors: string[] = [];
    if (failedApps > 0) {
      errors.push(`Apps: ${item.failedLinks!.apps!.join(', ')}`);
    }
    if (failedSuperTiles > 0) {
      errors.push(`Folders: ${item.failedLinks!.superTiles!.join(', ')}`);
    }

    const hasSuccesses = (item.linkedApps?.length || 0) > 0 || (item.linkedSuperTiles?.length || 0) > 0;

    // Determine status based on what failed
    let status: TileProcessingStatus;
    if (hasSuccesses) {
      // Some links succeeded, some failed - partial success
      status = TileProcessingStatus.WARNING_PARTIAL;
    } else {
      // All links failed - determine which type
      if (failedApps > 0 && failedSuperTiles > 0) {
        status = TileProcessingStatus.ERROR_LINKING; // Both types failed
      } else if (failedApps > 0) {
        status = TileProcessingStatus.ERROR_APP_LINKING; // Only app linking failed
      } else {
        status = TileProcessingStatus.ERROR_FOLDER_LINKING; // Only folder linking failed
      }
    }

    this.updateStatus(item.id, status, `Link failed: ${errors.join(', ')}`, item.tileId);
  }

  /**
   * Step 2 + 3 of formula tile creation:
   * POST a formula device with the config (linking it to the tile via Principals),
   * then PUT the tile to set its DeviceID.
   */
  private setupFormulaTile$(tileId: TileId, item: TileProcessingItem): Observable<TileId> {
    const config = item.formulaConfig!;
    config.Points[0].Principals = { TileIDs: [tileId] };

    return this.devicesService.createDevice$(makeFormulaDevice(config)).pipe(
      switchMap(createdDevice => {
        const updated = Tile.create(item.tile);
        updated.ID = tileId;
        (updated.Structure as SimpleAnalogStructure).DeviceID = createdDevice.ID!;
        return this.tilesService.saveTile$(updated).pipe(map(() => tileId));
      }),
    );
  }

  private assignTileTagsIfNeeded(item: TileProcessingItem, tileId: TileId): Observable<TileId> {
    const tagLabels = this.csvMapperService.parseTagLabels((item.csvData as any)[CsvFieldId.TAGS]);
    const facetPairs = this.csvMapperService.parseFacetPairs((item.csvData as any)[CsvFieldId.FACETS]);

    if ((tagLabels.length === 0 && facetPairs.length === 0) ||
        this.processingState() !== ProcessingState.PROCESSING) {
      return of(tileId);
    }

    this.updateStatus(item.id, TileProcessingStatus.LINKING_TAGS, undefined, tileId);

    const tagAssignments$ = tagLabels.length > 0
      ? forkJoin(tagLabels.map(label => {
          const cachedId = this.labelToTagId.get(label.toUpperCase());
          return cachedId
            ? of({ TagID: cachedId } as TagAssignment)
            : this.tagService.createOrFindTag$(label).pipe(
                tap(tag => this.labelToTagId.set(label.toUpperCase(), tag.ID)),
                map(tag => ({ TagID: tag.ID } as TagAssignment))
              );
        }))
      : of([] as TagAssignment[]);

    const profileKey = this.csvMapperService.resolveProfileKeyFromCsv(item.csvData);

    const facetAssignments$ = this.resolveFacetAssignments(facetPairs, profileKey);

    return forkJoin([tagAssignments$, facetAssignments$]).pipe(
      switchMap(([tagAssns, facetAssns]) =>
        this.tagService.assignTags$(ObjectType.TILE, tileId, [...tagAssns, ...facetAssns])
      ),
      map(() => tileId),
      catchError(error => {
        item.tagLinkingFailed = true;
        item.error = `ERROR_TAG_LINKING: ${error?.message ?? 'Tag linking failed'}`;
        return of(tileId);
      })
    );
  }

  private resolveFacetAssignments(
    facetPairs: {key: string; value: string}[],
    profileKey: string | null
  ): Observable<TagAssignment[]> {
    if (facetPairs.length === 0) return of([]);

    return defer(() => {
      const facets = profileKey
        ? TAG_PROFILES[profileKey].Facets
        : Object.values(TAG_PROFILES).flatMap(p => p.Facets);

      return of(facetPairs.map(({ key, value }) => {
        const f = facets.find(pf => pf.Label.toUpperCase() === key.toUpperCase());
        if (!f) throw new Error(`Facet "${key}" not found in profile`);
        return { TagID: f.ID, Value: value.toUpperCase() } as TagAssignment;
      }));
    });
  }

  private handleError(item: TileProcessingItem, error: any): void {
    if (error instanceof HttpErrorResponse && error.status === 403) {
      this.updateStatus(item.id, TileProcessingStatus.ERROR_PERMISSIONS, 'Permission denied');
    } else {
      const message = error instanceof HttpErrorResponse
        ? UtilsService.extractErrorMessage(error.error)
        : error?.message || null;
      this.updateStatus(item.id, TileProcessingStatus.ERROR_GENERAL, message || 'Creation failed');
    }
  }

  private storeMetadataInBackground(tileId: TileId, item: TileProcessingItem): void {
    const protocol = this.csvMapperService.detectProtocol(item.csvData);

    // Only store metadata for BACnet tiles
    if (protocol !== DeviceType.BACnet) {
      return;
    }

    const metadata = {
      discovery: {
        name: item.csvData[CsvFieldId.TILE_NAME] || '',
        description: item.csvData[CsvFieldId.DISPLAY_NAME] || item.csvData[CsvFieldId.TILE_NAME] || '',
        type: item.csvData[CsvFieldId.BACNET_OBJECT_TYPE] || '',
        address: item.csvData[CsvFieldId.BACNET_INSTANCE_NUMBER] || '',
        units: item.csvData[CsvFieldId.DATA_DISPLAY_UNITS] || '',
        pointId: JSON.stringify([item.csvData[CsvFieldId.BACNET_OBJECT_TYPE], item.csvData[CsvFieldId.BACNET_INSTANCE_NUMBER]])
      }
    };

    this.metadataService.patchPermanentMetadata$(tileId, metadata).subscribe({
      error: (err) => console.error('Failed to store metadata for tile', tileId, err)
    });
  }
}
